import { Stack, Typography } from '@mui/material';
import { RecruiterSearch, RecruiterSearchKeys } from 'features/co-pilot/co-pilot.types';
import { OptionType } from 'features/ui/base-components/pai-auto-complete';
import PaiMenuItem from 'features/ui/base-components/pai-menu-item';
import PaiSearchSelector from 'features/ui/base-components/pai-search-selector';
import PaiSelect from 'features/ui/base-components/pai-select';
import PaiTextArea from 'features/ui/base-components/pai-text-area';
import _ from 'lodash';
import React from 'react';
import { useTranslation } from 'react-i18next';
import ConstraintChip from './constraint-chip';
import ExperienceSlider from './experience-slider';
import { makeStyles } from 'tss-react/mui';

interface Props {
  constraints: RecruiterSearch;
  autoSyncLoading: boolean;
  loading: boolean;

  handleRecruiterConstraintQueryChange(searchType: RecruiterSearchKeys): (event: React.ChangeEvent<HTMLInputElement>) => void;

  searchConstraintsKeyUp(searchType: RecruiterSearchKeys): () => void;

  searchConstraintsKeyDown(): void;

  handleSelectRecruiterConstraint(searchType: RecruiterSearchKeys): (value: string) => () => void;

  handleRemoveRecruiterConstraint(searchType: RecruiterSearchKeys, urn: string): () => void;

  handleExcludeChange(searchType: RecruiterSearchKeys, urn: string): (value: boolean) => () => void;

  handleKeywordsChange(event: React.ChangeEvent<HTMLInputElement>): void;

  keywordKeyDown(event: React.KeyboardEvent): void;

  handleAreaChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handleTextFieldYearsOfExperienceChange(index: number): (event: React.ChangeEvent<HTMLInputElement>) => void;

  handleYearsOfExperienceChange(event: Event, newValue: number | number[], activeThumb: number): void;
  /** Recruiter only. Sales Nav Android has Region, no ZIP. */
  showPostalCode?: boolean;

  handleResetYearsOfExperience(): void;

  handleUpdateYearsOfExperience(): void;
}

const RecruiterSearchConstraints: React.FC<Props> = ({
  constraints,
  autoSyncLoading,
  loading,
  handleRecruiterConstraintQueryChange,
  searchConstraintsKeyUp,
  searchConstraintsKeyDown,
  handleSelectRecruiterConstraint,
  handleRemoveRecruiterConstraint,
  handleExcludeChange,
  handleKeywordsChange,
  keywordKeyDown,
  handleAreaChange,
  handleTextFieldYearsOfExperienceChange,
  handleYearsOfExperienceChange,
  handleResetYearsOfExperience,
  handleUpdateYearsOfExperience,
  showPostalCode = true,
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  const areaOptions: OptionType[] = [
    { label: t('within1Mi'), value: '1' },
    { label: t('within5Mi'), value: '5' },
    { label: t('within10Mi'), value: '10' },
    { label: t('within25Mi'), value: '25' },
    { label: t('within50Mi'), value: '50' },
    { label: t('within100Mi'), value: '100' },
    { label: t('within200Mi'), value: '200' },
  ];

  return (
    <Stack gap={3}>
      <div>
        <Typography className={classes.headerText} mb={'4px'}>
          {t('linkedInSearchFineTuner')}
        </Typography>
        <Typography className={classes.greyText}>{t('linkedInSearchDescription')}</Typography>
      </div>
      <Stack gap={1}>
        <PaiSearchSelector
          label={t('titles')}
          placeholder={t('enterTitleHere')}
          variant={'background'}
          value={constraints['occupation'].query}
          selectedValues={constraints['occupation'].selected.map((c) => c.value)}
          options={constraints['occupation'].options}
          onChange={handleRecruiterConstraintQueryChange('occupation')}
          onKeyUp={searchConstraintsKeyUp('occupation')}
          onKeyDown={searchConstraintsKeyDown}
          onMenuItemClick={handleSelectRecruiterConstraint('occupation')}
          disabled={constraints['occupation'].loading || loading || autoSyncLoading}
          loading={constraints['occupation'].loading}
        />
        {!_.isEmpty(constraints['occupation'].selected) ? (
          <Stack direction={'row'} gap={1} flexWrap={'wrap'}>
            {constraints['occupation'].selected.map((item) => (
              <ConstraintChip
                key={item.value}
                label={item.label}
                titleType={item.titleType}
                exclude={!!item.exclude}
                disabled={loading || autoSyncLoading}
                handleExcludeChange={handleExcludeChange('occupation', item.value)}
                handleRemoveRecruiterConstraint={handleRemoveRecruiterConstraint('occupation', item.value)}
              />
            ))}
          </Stack>
        ) : null}
      </Stack>
      <PaiTextArea
        label={t('keywords')}
        placeholder={t('enterKeywordsHere')}
        variant={'background'}
        value={constraints.keywords}
        rows={5}
        onChange={handleKeywordsChange}
        onKeyDown={keywordKeyDown}
        disabled={loading || autoSyncLoading}
      />
      <Stack gap={1}>
        <PaiSearchSelector
          label={t('industry')}
          placeholder={t('enterIndustryHere')}
          variant={'background'}
          value={constraints['industry'].query}
          selectedValues={constraints['industry'].selected.map((c) => c.value)}
          options={constraints['industry'].options}
          onChange={handleRecruiterConstraintQueryChange('industry')}
          onKeyUp={searchConstraintsKeyUp('industry')}
          onKeyDown={searchConstraintsKeyDown}
          onMenuItemClick={handleSelectRecruiterConstraint('industry')}
          disabled={constraints['industry'].loading || loading || autoSyncLoading}
          loading={constraints['industry'].loading}
        />
        {!_.isEmpty(constraints['industry'].selected) ? (
          <Stack direction={'row'} gap={1} flexWrap={'wrap'}>
            {constraints['industry'].selected.map((item) => (
              <ConstraintChip
                key={item.value}
                label={item.label}
                titleType={item.titleType}
                disabled={loading || autoSyncLoading}
                handleRemoveRecruiterConstraint={handleRemoveRecruiterConstraint('industry', item.value)}
              />
            ))}
          </Stack>
        ) : null}
      </Stack>
      <Stack gap={1}>
        <PaiSearchSelector
          label={t('company')}
          placeholder={t('enterCompanyHere')}
          variant={'background'}
          value={constraints['company'].query}
          selectedValues={constraints['company'].selected.map((c) => c.value)}
          options={constraints['company'].options}
          onChange={handleRecruiterConstraintQueryChange('company')}
          onKeyUp={searchConstraintsKeyUp('company')}
          onKeyDown={searchConstraintsKeyDown}
          onMenuItemClick={handleSelectRecruiterConstraint('company')}
          disabled={constraints['company'].loading || loading || autoSyncLoading}
          loading={constraints['company'].loading}
        />
        {!_.isEmpty(constraints['company'].selected) ? (
          <Stack direction={'row'} gap={1} flexWrap={'wrap'}>
            {constraints['company'].selected.map((item) => (
              <ConstraintChip
                key={item.value}
                label={item.label}
                titleType={item.titleType}
                exclude={!!item.exclude}
                disabled={loading || autoSyncLoading}
                handleExcludeChange={handleExcludeChange('company', item.value)}
                handleRemoveRecruiterConstraint={handleRemoveRecruiterConstraint('company', item.value)}
              />
            ))}
          </Stack>
        ) : null}
      </Stack>
      <Stack gap={1}>
        <PaiSearchSelector
          label={t('location')}
          placeholder={t('enterLocationHere')}
          variant={'background'}
          value={constraints['geo'].query}
          selectedValues={constraints['geo'].selected.map((c) => c.value)}
          options={constraints['geo'].options}
          onChange={handleRecruiterConstraintQueryChange('geo')}
          onKeyUp={searchConstraintsKeyUp('geo')}
          onKeyDown={searchConstraintsKeyDown}
          onMenuItemClick={handleSelectRecruiterConstraint('geo')}
          disabled={constraints['geo'].loading || loading || autoSyncLoading}
          loading={constraints['geo'].loading}
        />
        {!_.isEmpty(constraints['geo'].selected) ? (
          <Stack direction={'row'} gap={1} flexWrap={'wrap'}>
            {constraints['geo'].selected.map((item) => (
              <ConstraintChip
                key={item.value}
                label={item.label}
                titleType={item.titleType}
                exclude={!!item.exclude}
                disabled={loading || autoSyncLoading}
                handleExcludeChange={handleExcludeChange('geo', item.value)}
                handleRemoveRecruiterConstraint={handleRemoveRecruiterConstraint('geo', item.value)}
              />
            ))}
          </Stack>
        ) : null}
      </Stack>
      {showPostalCode ? (
        <>
          <Stack gap={1}>
            <PaiSearchSelector
              label={t('zip')}
              placeholder={t('enterZipHere')}
              variant={'background'}
              value={constraints['zip'].query}
              selectedValues={constraints['zip'].selected.map((c) => c.value)}
              options={constraints['zip'].options}
              onChange={handleRecruiterConstraintQueryChange('zip')}
              onKeyUp={searchConstraintsKeyUp('zip')}
              onKeyDown={searchConstraintsKeyDown}
              onMenuItemClick={handleSelectRecruiterConstraint('zip')}
              disabled={constraints['zip'].loading || loading || autoSyncLoading}
              loading={constraints['zip'].loading}
            />
            {!_.isEmpty(constraints['zip'].selected) ? (
              <Stack direction={'row'} gap={1} flexWrap={'wrap'}>
                {constraints['zip'].selected.map((item) => (
                  <ConstraintChip
                    key={item.value}
                    label={item.label}
                    titleType={item.titleType}
                    exclude={!!item.exclude}
                    disabled={loading || autoSyncLoading}
                    handleExcludeChange={handleExcludeChange('zip', item.value)}
                    handleRemoveRecruiterConstraint={handleRemoveRecruiterConstraint('zip', item.value)}
                  />
                ))}
              </Stack>
            ) : null}
          </Stack>
          {!_.isEmpty(constraints['zip'].selected) ? (
            <PaiSelect
              label={t('area')}
              variant={'background'}
              value={constraints.area}
              disabled={loading || autoSyncLoading}
              onChange={handleAreaChange}
            >
              {areaOptions.map((option) => (
                <PaiMenuItem key={option.value} value={option.value}>
                  {option.label}
                </PaiMenuItem>
              ))}
            </PaiSelect>
          ) : null}
        </>
      ) : null}
      <Stack gap={1}>
        <PaiSearchSelector
          label={t('skills')}
          placeholder={t('enterSkillHere')}
          variant={'background'}
          value={constraints['skill'].query}
          selectedValues={constraints['skill'].selected.map((c) => c.value)}
          options={constraints['skill'].options}
          onChange={handleRecruiterConstraintQueryChange('skill')}
          onKeyUp={searchConstraintsKeyUp('skill')}
          onKeyDown={searchConstraintsKeyDown}
          onMenuItemClick={handleSelectRecruiterConstraint('skill')}
          disabled={constraints['skill'].loading || loading || autoSyncLoading}
          loading={constraints['skill'].loading}
        />
        {!_.isEmpty(constraints['skill'].selected) ? (
          <Stack direction={'row'} gap={1} flexWrap={'wrap'}>
            {constraints['skill'].selected.map((item) => (
              <ConstraintChip
                key={item.value}
                label={item.label}
                titleType={item.titleType}
                exclude={!!item.exclude}
                disabled={loading || autoSyncLoading}
                handleExcludeChange={handleExcludeChange('skill', item.value)}
                handleRemoveRecruiterConstraint={handleRemoveRecruiterConstraint('skill', item.value)}
              />
            ))}
          </Stack>
        ) : null}
      </Stack>
      <ExperienceSlider
        value={constraints.yearsOfExperience}
        loading={loading || autoSyncLoading}
        handleTextFieldValueChange={handleTextFieldYearsOfExperienceChange}
        handleValueChange={handleYearsOfExperienceChange}
        handleResetYearsOfExperience={handleResetYearsOfExperience}
        handleUpdateYearsOfExperience={handleUpdateYearsOfExperience}
      />
    </Stack>
  );
};

const useStyles = makeStyles()((theme) => ({
  headerText: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 'bold',
  },
  greyText: {
    fontSize: theme.typography.pxToRem(15),
    color: theme.palette.greys.medium,
  },
}));

export default RecruiterSearchConstraints;
